import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { VFC } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import HoverableDraggable from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/HoverableDraggable';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { reorder } from 'dashboard/lib/dnd/utils';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  FakeWorkflowStepType,
  newPlaceholderStep,
  WorkflowStep,
  WorkflowStepParallel,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing[4]};
`;

const BranchFlexRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const BranchFlexColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const RouteList = styled.div<{ isDraggingOver: boolean; removeMargin: boolean }>`
  ${({ isDraggingOver, removeMargin }) => css`
    margin-left: ${removeMargin ? -32 : 0}px;
    margin-right: ${removeMargin ? -32 : 0}px;
    background-color: ${isDraggingOver ? COLORS.indigo10 : COLORS.white};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
`;

interface WorkflowConfigurationParallelProps {
  step: WorkflowStepParallel;
  editable: boolean;
  errors: WorkflowError[];
  updateStep: (newStep: WorkflowStep) => void;
}

const TYPE_TO_NAME: Record<WorkflowStepType | FakeWorkflowStepType, string> = {
  [WorkflowStepType.Action]: 'Action',
  [WorkflowStepType.Conditional]: 'Conditional',
  [WorkflowStepType.Input]: 'Input',
  [WorkflowStepType.Output]: 'Output',
  [WorkflowStepType.Loop]: 'Loop',
  [WorkflowStepType.Module]: 'Module',
  [WorkflowStepType.Parallel]: 'Parallel',
  [WorkflowStepType.Placeholder]: 'Placeholder',
  [WorkflowStepType.Wait]: 'Wait',
  [FakeWorkflowStepType.MapOutput]: 'Map Output',
} as const;

const StepConfigParallel: VFC<WorkflowConfigurationParallelProps> = ({
  step,
  editable,
  errors,
  updateStep,
}) => {
  const { branches } = step;

  const handleAdd = () => {
    updateStep({
      ...step,
      branches: [...branches, [newPlaceholderStep()]],
    });
  };

  const { renderErrorMessageFor } = useWorkflowError<'branches'>(errors);

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const newBranches = reorder(branches, result.source.index, result.destination.index);
    updateStep({
      ...step,
      branches: newBranches,
    });
  };

  const showDeleteButton = branches.length > 1;
  const deleteBranch = (index: number) => {
    updateStep({
      ...step,
      branches: [...branches.slice(0, index), ...branches.slice(index + 1)],
    });
  };

  return (
    <Container>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='droppable'>
          {(droppableProvided, droppableSnapshot) => (
            <RouteList
              ref={droppableProvided.innerRef}
              isDraggingOver={droppableSnapshot.isDraggingOver}
              removeMargin={editable}
            >
              {branches.map((branch, index) => {
                return (
                  <HoverableDraggable
                    draggableId={`branch-${index}`}
                    index={index}
                    key={`branch-${index}`}
                    isDragDisabled={!editable}
                  >
                    <BranchFlexColumn>
                      <BranchFlexRow>
                        <div>
                          <Heading size='sm'>Branch {index + 1}</Heading>
                        </div>
                        <Button
                          variant='danger'
                          width='icon'
                          type='button'
                          onClick={() => deleteBranch(index)}
                          id='step-config-parallel__delete-branch-button'
                          aria-label={`delete branch ${index + 1}`}
                          disabled={!showDeleteButton}
                        >
                          <TrashIcon />
                        </Button>
                      </BranchFlexRow>
                      <Spacer height={1} />
                      {branch.length > 0 && (
                        <Paragraph size='sm'>
                          {branch[0].name} ({TYPE_TO_NAME[branch[0].type]})
                        </Paragraph>
                      )}
                    </BranchFlexColumn>
                  </HoverableDraggable>
                );
              })}
              {droppableProvided.placeholder}
            </RouteList>
          )}
        </Droppable>
      </DragDropContext>

      <BranchFlexRow>
        {editable && (
          <Button
            id='parallel-step__new-branch-button'
            type='button'
            variant='basic'
            width='auto'
            onClick={handleAdd}
          >
            New Branch
          </Button>
        )}
      </BranchFlexRow>
      {renderErrorMessageFor('branches')}
    </Container>
  );
};

export default StepConfigParallel;
